import { Box, Flex, Icon, Image, SimpleGrid, Text, useDisclosure } from '@chakra-ui/react';
import { FiCamera } from '@react-icons/all-files/fi/FiCamera';

import Modal from '../../../../../../components/Modal';
import Button from '../../../../../../components/ui/buttons/Button';
import MarketplaceCard from '../../MarketplaceCard';
import { CATEGORIES, PHOTO_INPUT_ACCEPT, PHOTO_SLOTS } from '../../photos/constants';
import { usePhotoUpload } from '../../photos/hooks/usePhotoUpload';
import ShotTile from './ShotTile';

type CarPhotosPanelProps = {
  dealId: string;
};

const THUMB_TILE_COUNT = 4;

const CarPhotosPanel = ({ dealId }: CarPhotosPanelProps) => {
  const { photos, processingSlotIds, fileInputRef, handleSlotClick, handleFileSelected } =
    usePhotoUpload(dealId);
  const { isOpen: isGalleryOpen, onOpen: openGallery, onClose: closeGallery } = useDisclosure();

  const filledSlots = PHOTO_SLOTS.flatMap((slot) => {
    const entry = photos.get(slot.id);
    return entry ? [{ slot, entry }] : [];
  });
  const hasEmptySlots = filledSlots.length < PHOTO_SLOTS.length;
  const isUploading = processingSlotIds.size > 0;

  const previewSlots = filledSlots.slice(0, THUMB_TILE_COUNT);
  const hiddenCount = filledSlots.length - THUMB_TILE_COUNT;

  return (
    <MarketplaceCard bg="grayLightest" p="16px 18px">
      <input
        ref={fileInputRef}
        type="file"
        accept={PHOTO_INPUT_ACCEPT}
        hidden
        onChange={handleFileSelected}
      />
      <Flex align="center" justify="space-between" gap="12px">
        <Text fontFamily="heading" fontSize="14px" fontWeight="700" color="leaseEndBlue">
          Car photos
        </Text>
        <Button
          variant="link"
          fontSize="12px"
          fontWeight={700}
          color="oceanBoatBlue"
          onClick={openGallery}
        >
          View all
        </Button>
      </Flex>

      <SimpleGrid columns={THUMB_TILE_COUNT} spacing="6px" mt="12px">
        {previewSlots.map(({ slot, entry }, index) => {
          const isOverflowTile = index === THUMB_TILE_COUNT - 1 && hiddenCount > 0;
          return (
            <Box
              key={slot.id}
              as="button"
              type="button"
              onClick={openGallery}
              position="relative"
              aspectRatio="1 / 1"
              borderRadius="8px"
              overflow="hidden"
              bg="white"
            >
              <Image src={entry.previewUrl} alt={slot.label} w="100%" h="100%" objectFit="cover" />
              {isOverflowTile && (
                <Flex
                  position="absolute"
                  inset={0}
                  bg="blackAlpha.600"
                  align="center"
                  justify="center"
                >
                  <Text color="white" fontSize="13px" fontWeight={700}>
                    +{hiddenCount + 1}
                  </Text>
                </Flex>
              )}
            </Box>
          );
        })}
      </SimpleGrid>

      {hasEmptySlots && (
        <Button
          variant="dashed"
          w="100%"
          mt="12px"
          leftIcon={<Icon as={FiCamera} boxSize="14px" />}
          isLoading={isUploading}
          onClick={openGallery}
        >
          Add a photo
        </Button>
      )}

      <Modal
        isOpen={isGalleryOpen}
        onClose={closeGallery}
        title="Car photos"
        titleFontSize="22px"
        titleFontWeight={700}
        closeOnOverlayClick={!isUploading}
      >
        <Flex direction="column" gap={5} pb={4}>
          {hasEmptySlots && (
            <Text fontSize="14px" color="charcoal">
              Tap an empty shot to capture it. Photos save as you go.
            </Text>
          )}
          <SimpleGrid columns={2} spacing="10px">
            {PHOTO_SLOTS.map((slot) => {
              const entry = photos.get(slot.id);
              return entry ? (
                <Box
                  key={slot.id}
                  borderRadius="12px"
                  borderWidth="1px"
                  borderColor="platinum"
                  bg="white"
                  overflow="hidden"
                >
                  <Image
                    src={entry.previewUrl}
                    alt={slot.label}
                    w="100%"
                    h="120px"
                    objectFit="cover"
                  />
                  <Text px="10px" py="8px" fontSize="13px" fontWeight={700} color="charcoal">
                    {slot.label}
                  </Text>
                </Box>
              ) : (
                <ShotTile
                  key={slot.id}
                  label={slot.label}
                  hint={CATEGORIES.find((category) => category.key === slot.category)?.title ?? ''}
                  isUploading={processingSlotIds.has(slot.id)}
                  disabled={isUploading}
                  onTap={() => handleSlotClick(slot.id)}
                />
              );
            })}
          </SimpleGrid>
        </Flex>
      </Modal>
    </MarketplaceCard>
  );
};

export default CarPhotosPanel;
